import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/**
 * The client-owned escape option appended to every question: "Other / none of these".
 * Ticked with text = the user's own answer; ticked without text = none of the options
 * matched. The id is unchanged from the earlier exclusive "None of the above" so saved
 * sessions that recorded it still render.
 */
export const INTAKE_NONE_OPTION_ID = '__none__';

/** The intents an option may carry (question-vs-change disambiguation). */
const OPTION_INTENTS = new Set(['ask', 'change']);

/**
 * Parses an intake-questions JSON string ({ questions: [{ id, category, text,
 * allowMultiple, options: [{ id, text, intent? }] }] }) into a normalized array, or null
 * when the payload is not a question round. The server is the authoritative normalizer —
 * this only guards against a malformed round-trip.
 */
export const parseIntakeQuestions = (json) => {
  try {
    const root = JSON.parse(safeString(json) || 'null');
    if (!root || !Array.isArray(root.questions)) return null;
    const questions = root.questions
      .map((q) => ({
        id: safeString(q?.id).trim(),
        category: safeString(q?.category).trim(),
        text: safeString(q?.text).trim(),
        allowMultiple: q?.allowMultiple !== false,
        options: (Array.isArray(q?.options) ? q.options : [])
          .map((o) => {
            const intent = safeString(o?.intent).trim().toLowerCase();
            return {
              id: safeString(o?.id).trim(),
              text: safeString(o?.text).trim(),
              ...(OPTION_INTENTS.has(intent) ? { intent } : {}),
            };
          })
          .filter((o) => o.id && o.text),
      }))
      .filter((q) => q.id && q.text && q.options.length >= 2);
    return questions.length > 0 ? questions : null;
  } catch {
    return null;
  }
};

/**
 * Renders one question's answer as the prose line the assistant reads:
 * "<question> — <picked option texts>[; Other: <text>]" or "<question> — None of the above".
 */
export const formatIntakeAnswerLine = (question, optionIds, otherText) => {
  const chosen = Array.isArray(optionIds) ? optionIds : [];
  const picked = (question?.options ?? []).filter((o) => chosen.includes(o.id)).map((o) => o.text);
  const other = safeString(otherText).trim();
  const parts = [...picked];
  if (chosen.includes(INTAKE_NONE_OPTION_ID) && other) parts.push(`${t('Other')}: ${other}`);
  const answer = parts.length > 0 ? parts.join('; ') : t('None of the above');
  return `${safeString(question?.text)} — ${answer}`;
};

/**
 * The intent the user's picks imply, when the question's options carry one: the single
 * distinct intent among picked options, or null when none (or several) apply.
 */
export const resolveIntakeIntent = (questions, selections) => {
  const intents = new Set();
  for (const q of Array.isArray(questions) ? questions : []) {
    const chosen = new Set(selections?.[q.id] ?? []);
    for (const o of q.options ?? []) {
      if (o.intent && chosen.has(o.id)) intents.add(o.intent);
    }
  }
  return intents.size === 1 ? [...intents][0] : null;
};

const checkboxRowStyle = (checked, disabled) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '4px 8px',
  borderRadius: 4,
  background: checked ? '#e7f1ff' : 'transparent',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 13,
  color: '#212529',
  lineHeight: 1.4,
});

/**
 * One round of the assistant's clarifying questions, rendered as checkbox groups on the
 * assistant's message: the user ticks the options that apply (several where the
 * question allows it) and submits — the selections become the next chat turn, so the
 * assistant reasons over them like any other answer. Every question gets a client-added
 * "Other / none of these" row with a free-text box, so the user is never boxed in by the
 * offered options. Submit requires every question answered (the Other row counts).
 *
 * Props:
 * - questions: [{ id, category, text, allowMultiple, options: [{ id, text, intent? }] }]
 * - answered?: true when this round was already submitted (frozen rendering)
 * - selections?: { [questionId]: [optionId] } — the submitted picks (for frozen rendering)
 * - otherText?: { [questionId]: string } — the submitted free text (for frozen rendering)
 * - disabled?: while a turn is running
 * - templateCatalog?: the prompt composer's right-click catalog; when given, the Other
 *   box is a template text area so products / lookups / references insert as the same
 *   resolved literals they would in the prompt ("Roller Shutter (ProductID <guid>)")
 * - onSubmit(selections, otherText): called with { [questionId]: [optionId] } covering
 *   every question and { [questionId]: string } for the typed answers
 */
export const IntakeQuestionsCard = ({ questions, answered, selections, otherText, disabled, templateCatalog, onSubmit }) => {
  const list = Array.isArray(questions) ? questions : [];
  const [picks, setPicks] = React.useState({});
  const [texts, setTexts] = React.useState({});
  const frozen = !!answered;
  const current = frozen ? (selections ?? {}) : picks;
  const currentTexts = frozen ? (otherText ?? {}) : texts;

  const toggle = (question, optionId) => {
    if (frozen || disabled) return;
    setPicks((prev) => {
      const chosen = new Set(prev[question.id] ?? []);
      if (chosen.has(optionId)) {
        chosen.delete(optionId);
      } else {
        if (!question.allowMultiple) chosen.clear();
        chosen.add(optionId);
      }
      return { ...prev, [question.id]: [...chosen] };
    });
  };

  // Typing an answer ticks the Other row (and, on single-select questions, replaces
  // the option pick) so the text is never submitted silently.
  const setOtherText = (question, value) => {
    if (frozen || disabled) return;
    setTexts((prev) => ({ ...prev, [question.id]: value }));
    if (safeString(value).trim()) {
      setPicks((prev) => {
        const chosen = new Set(prev[question.id] ?? []);
        if (chosen.has(INTAKE_NONE_OPTION_ID)) return prev;
        if (!question.allowMultiple) chosen.clear();
        chosen.add(INTAKE_NONE_OPTION_ID);
        return { ...prev, [question.id]: [...chosen] };
      });
    }
  };

  const allAnswered = list.every((q) => (current[q.id] ?? []).length > 0);

  if (list.length === 0) return null;

  return (
    <div
      data-tagname="intakeQuestionsCard"
      style={{
        border: '1px solid #cfe2ff',
        borderRadius: 8,
        background: '#f8fbff',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 640,
      }}
    >
      {list.map((question) => {
        const chosen = new Set(current[question.id] ?? []);
        const otherChecked = chosen.has(INTAKE_NONE_OPTION_ID);
        const typed = safeString(currentTexts[question.id]);
        return (
          <div key={question.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
              {question.category && (
                <span style={{
                  display: 'inline-block',
                  marginRight: 8,
                  padding: '0px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: '#e7f1ff',
                  color: '#0a58ca',
                  whiteSpace: 'nowrap',
                }}
                >
                  {question.category}
                </span>
              )}
              {question.text}
              {question.allowMultiple && !frozen && (
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: '#6c757d' }}>
                  {t('(tick all that apply)')}
                </span>
              )}
            </div>
            {question.options.map((option) => {
              const checked = chosen.has(option.id);
              // Frozen cards only show what was picked plus muted unpicked rows.
              return (
                <label key={option.id} style={checkboxRowStyle(checked, frozen || disabled)}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={frozen || !!disabled}
                    onChange={() => toggle(question, option.id)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ opacity: frozen && !checked ? 0.55 : 1 }}>
                    {option.text}
                  </span>
                </label>
              );
            })}
            <label style={checkboxRowStyle(otherChecked, frozen || disabled)}>
              <input
                type="checkbox"
                data-tagname="intakeOther"
                checked={otherChecked}
                disabled={frozen || !!disabled}
                onChange={() => toggle(question, INTAKE_NONE_OPTION_ID)}
                style={{ marginTop: 2 }}
              />
              <span style={{ opacity: frozen && !otherChecked ? 0.55 : 1, fontStyle: 'italic' }}>
                {t('Other / none of these')}
              </span>
            </label>
            {(otherChecked || (!frozen && typed.trim())) && (
              frozen ? (
                typed.trim() && (
                  <div style={{ marginLeft: 32, fontSize: 13, color: '#212529', whiteSpace: 'pre-wrap' }}>
                    {typed}
                  </div>
                )
              ) : (
                <div style={{ marginLeft: 32, fontSize: 13 }}>
                  <AccentTemplateTextAreaUnbound
                    dataTagName="intakeOtherText"
                    rows={2}
                    max={2000}
                    defaultValue={typed}
                    disabled={!!disabled}
                    placeholder={t('Type your own answer, or leave blank for none of these')}
                    templateCatalog={templateCatalog}
                    insertMode="snippet"
                    style={{ width: '100%' }}
                    onChange={(value) => setOtherText(question, safeString(value))}
                  />
                </div>
              )
            )}
          </div>
        );
      })}

      {!frozen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AccentButton
            tagName="intakeSubmit"
            disabled={!!disabled || !allAnswered}
            onClick={(e) => {
              onSubmit?.({ ...picks }, { ...texts });
              e?.promise?.();
            }}
            tooltip={allAnswered
              ? t('Send these answers to the assistant.')
              : t('Answer every question (or tick Other / none of these) to continue.')}
          >
            {t('Submit answers')}
          </AccentButton>
          {!allAnswered && (
            <span style={{ fontSize: 12, color: '#6c757d' }}>
              {t('Answer every question to continue — "Other / none of these" counts.')}
            </span>
          )}
        </div>
      )}
      {frozen && (
        <div style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>
          {t('Answers sent.')}
        </div>
      )}
    </div>
  );
};
